import type { ReactElement } from 'react';
import { Select } from 'src/components/ui/select';
import { portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import {
  getRespondentScopeVisibility,
  listBreakoutAttributes,
  type RespondentScope,
} from '../../services/demographic-breakout.helpers';
import type { SurveyStats } from 'src/redux/survey/survey.types';

interface BreakoutControlsProps {
  stats?: SurveyStats;
  breakoutAttribute: string | null;
  respondentScope: RespondentScope;
  onBreakoutChange: (attributeKey: string | null) => void;
  onRespondentScopeChange: (scope: RespondentScope) => void;
}

const SCOPE_OPTIONS: Array<{ value: RespondentScope; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'ai', label: 'AI' },
  { value: 'human', label: 'Human' },
];

function BreakoutControls({
  stats,
  breakoutAttribute,
  respondentScope,
  onBreakoutChange,
  onRespondentScopeChange,
}: BreakoutControlsProps): ReactElement {
  const attributes = listBreakoutAttributes(stats, respondentScope);
  const { showScopeControl } = getRespondentScopeVisibility(stats ?? {
    extSurveyId: '',
    totalUsers: 0,
    aiUsers: 0,
    humanUsers: 0,
    rtrUsers: 0,
    aiPercentage: 0,
    humanPercentage: 0,
    rtrPercentage: 0,
    totalPercentage: 0,
    title: '',
    displayName: '',
    questions: [],
  });

  const breakoutOptions = [
    { value: '', label: 'None' },
    ...attributes.map((attribute) => ({
      value: attribute.key,
      label: attribute.label,
    })),
  ];

  return (
    <div className="flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-2">
        <span className={portalDs.filterLabel}>
          Break out by
        </span>
        <Select
          aria-label="Break out by demographic attribute"
          className={cn(portalDs.select, 'min-w-[180px]')}
          value={breakoutAttribute ?? ''}
          onValueChange={(value) => onBreakoutChange(value === '' ? null : value)}
          options={breakoutOptions}
        />
      </div>

      {showScopeControl ? (
        <div
          className={portalDs.segmentGroup}
          role="group"
          aria-label="Respondent scope"
        >
          {SCOPE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={cn(
                portalDs.segment,
                respondentScope === option.value && portalDs.segmentActive,
              )}
              onClick={() => onRespondentScopeChange(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default BreakoutControls;
